import type { Language } from '@eformulieren/engine'
import type { ReactNode } from 'react'
import { word } from '../../language/words.js'
import { RequiredMark } from '../RequiredMark.js'
import type { SingleProps, Stamp } from './kinds.js'
import { formatDateTime } from '../../language/text.js'

/**
 * Ja of nee: `toggle`.
 *
 * Eén aanvinkregel met de vraag als label, en de waarde is `true` of `false`. Niet
 * aangeraakt is niet hetzelfde als nee: dan staat er niets in de stand, zodat
 * `verplicht` erop kan slaan (R-26). Vandaar dat dit een aanvinkregel is en niet
 * twee radioknoppen met een voorgeselecteerde nee.
 *
 * **Het akkoordveld is een variant hiervan** (R-19, R-89): zet de invuller hem op
 * Ja, dan staat er wie dat deed en wanneer, niet bewerkbaar, en op Nee is die stempel
 * weg. De stempel is de herkomst van het veld (`vld-x@door`, `vld-x@op`), en die zet
 * de pod uit het token van de verbinding en nooit uit wat de client beweert
 * (`apps/runtime/src/origin.ts`, R-154); de renderer leest hem en geeft hem als
 * `stempel` mee zolang de waarde Ja is. Deze control tekent hem alleen, en zonder persoon: "door u"
 * als de lezer het zelf was, anders "door een andere invuller" (zie `Stamp`). De regelsoort
 * `akkoordJa` maakt van het akkoord een poort op indienen (B11).
 *
 * **De stempel wordt voorgelezen** (R-139, R-140): hij hangt aan het vinkje via
 * `aria-describedby`, naast wat de veldschil daar al koppelt, en hij staat in een live
 * region die er altijd is zodra het een akkoordveld is. Dat laatste is nodig omdat de
 * stempel pas na het aanvinken komt; een live region die tegelijk met zijn inhoud in de
 * pagina verschijnt, kondigen schermlezers niet aan.
 */
export function Toggle(props: SingleProps): ReactNode {
  const { veld: field, koppeling: link, waarde: value, onWaarde: onValue, bewerkbaar: editable, taal: language } = props
  const consent = 'akkoord' in field && field.akkoord === true
  // Alleen bij Ja: een stempel bij Nee zou een akkoord tonen dat er niet is.
  const stamp = consent && value === true ? props.stempel : undefined
  const stampId = `${link.id}-stempel`
  const textId = `${link.id}-tekst`
  const describedBy =
    stamp === undefined
      ? link['aria-describedby']
      : [link['aria-describedby'], stampId].filter((part) => part !== undefined).join(' ')
  if (consent && props.doorIndiener === true) {
    // Het akkoord van de indiener, voor wie het niet zelf geeft (besluit van 3 november 2026, R-69): de
    // verklaring als tekst en wat ermee is gebeurd, en geen grijs, uitgeschakeld vinkje (12.16, R-86).
    // Een vinkje dat er staat maar niet werkt, nodigt uit tot klikken; deze lezer heeft hier niets te
    // doen. De stand staat in dezelfde live region als bij het vinkje, zodat een akkoord dat de indiener
    // intussen geeft wordt voorgelezen (R-139, R-140).
    return (
      <span className="ef-schakelaar" data-akkoord="ja" data-door-indiener="ja" id={link.id}>
        <span className="ef-schakelaar__tekst" id={textId}>
          {field.label === undefined ? word('akkoordVink', language) : field.label[language]}
        </span>
        <span className="ef-schakelaar__stempel" role="status">
          {stamp === undefined ? <span id={stampId}>{word('akkoordDoorIndiener', language)}</span> : <StampText id={stampId} stamp={stamp} language={language} />}
        </span>
      </span>
    )
  }
  if (!editable && !consent) {
    // Een ja-of-neevraag om te lezen is een antwoord, "Ja" of "Nee", en geen uitgeschakeld vinkje (R-86),
    // zoals elk keuzeveld dat alleen te lezen is. Het akkoord houdt zijn vinkje met de verklaring en de
    // stempel: daar is het vinkje zelf de verklaring (R-89).
    return <input className="ef-invoer" type="text" {...link} readOnly value={value === true ? word('ja', language) : value === false ? word('nee', language) : ''} />
  }
  return (
    // Het vinkje met een zichtbare tekst ernaast, samen één `label` en dus één raakvlak (audit van
    // 30 oktober 2026, principe 15, WCAG 3.3.2 en 2.5.8). Eerder stond er een kaal vinkje van 13
    // pixels zonder tekst: wat aangevinkt betekende, stond nergens. De naam blijft de vraag in de
    // kop van de veldschil, via `aria-labelledby` (`HEADER_VIA_ARIA`); de tekst hier zegt wat
    // aanvinken betekent.
    <span className="ef-schakelaar" data-akkoord={consent ? 'ja' : undefined}>
      <label className="ef-schakelaar__regel">
        <input
          type="checkbox"
          className="ef-schakelaar__vinkje"
          {...link}
          // De vraag en dan de tekst ernaast ("… Ja"), zodat de zichtbare tekst in de naam staat
          // (WCAG 2.5.3).
          // Bij een akkoord is de tekst ernaast de verklaring zelf en dus de hele naam.
          aria-labelledby={consent ? textId : [link['aria-labelledby'], textId].filter((part) => part !== undefined).join(' ')}
          aria-describedby={describedBy}
          checked={value === true}
          disabled={!editable}
          onChange={(event) => onValue(event.target.checked)}
        />
        <span className="ef-schakelaar__tekst" id={textId}>
          {consent && field.label !== undefined ? field.label[language] : word(consent ? 'akkoordVink' : 'ja', language)}
        </span>
        {/*
          Een akkoord heeft geen zichtbare kop, dus het sterretje staat hier naast de verklaring en
          niet in de schil (besluit van 1 november 2026, R-09, R-89). Buiten de tekst waar de naam
          vandaan komt, en `aria-hidden`: de schermlezer hoort "verplicht" via `aria-required`.
        */}
        {consent && editable && link['aria-required'] === true ? <RequiredMark /> : null}
      </label>
      {consent && (
        <span className="ef-schakelaar__stempel" role="status">
          {stamp !== undefined && <StampText id={stampId} stamp={stamp} language={language} />}
        </span>
      )}
    </span>
  )
}

function StampText({ id, stamp, language }: { id: string; stamp: Stamp; language: Language }): ReactNode {
  return (
    <span id={id}>
      {word(stamp.eigen ? 'akkoordDoorU' : 'akkoordDoorAnder', language)} {word('akkoordOp', language)}{' '}
      <time dateTime={stamp.moment}>{formatMoment(stamp.moment, language)}</time>
    </span>
  )
}

/**
 * Het moment in de taal van de lezer, of onbewerkt als de omgeving er niets van maakt.
 *
 * Tot op de minuut en niet op de seconde: de pod zet het moment nog recht nadat de browser het
 * zijn eigen klok gaf, en een tekst die daarbij om een paar seconden verspringt zou de live region
 * het akkoord twee keer laten voorlezen.
 */
function formatMoment(moment: string, language: Language): string {
  return formatDateTime(moment, language, {
    dateStyle: 'long',
    timeStyle: 'short',
  })
}
